import React, { useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@vehix/portal/components/ui/sheet';
import { ScrollArea } from '@vehix/portal/components/ui/scroll-area';
import { cn } from '@vehix/portal/lib/utils';
import {
  oemFromTaskName,
  dcZoneFromTaskName
} from '@vehix/shared/admin/hooks/useRecallSnapshotMonitoring';
import type {
  AssetStatus,
  PerOemAsset,
  RefreshExecutionRow
} from '@vehix/shared/types/recallSnapshots';

interface Props {
  asset: PerOemAsset | null;
  executions: RefreshExecutionRow[];
  onClose: () => void;
}

const STATUS_PILL: Record<AssetStatus, string> = {
  healthy: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
  stale: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300'
};

const EXEC_STATUS_DOT: Record<string, string> = {
  completed: 'bg-emerald-500',
  failed: 'bg-red-500',
  data_collection: 'bg-amber-400',
  vin_processing: 'bg-amber-400',
  running: 'bg-amber-400',
  pending: 'bg-muted-foreground/40',
  queued: 'bg-muted-foreground/40'
};

function fmtDuration(s: number | null): string {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

interface DcZoneSummary {
  dc_zone: string;
  latest_at: string | null;
  latest_status: string | null;
  latest_duration: number | null;
  latest_error: Record<string, unknown> | null;
  recent: RefreshExecutionRow[];
}

function summarizeByDcZone(executions: RefreshExecutionRow[]): DcZoneSummary[] {
  const byZone = new Map<string, RefreshExecutionRow[]>();
  for (const e of executions) {
    const dc = dcZoneFromTaskName(e.actor_task_name) ?? e.actor_task_name;
    const arr = byZone.get(dc) ?? [];
    arr.push(e);
    byZone.set(dc, arr);
  }
  const summaries: DcZoneSummary[] = [];
  for (const [dc_zone, runs] of byZone.entries()) {
    runs.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    const latest = runs[0];
    summaries.push({
      dc_zone,
      latest_at: latest?.created_at ?? null,
      latest_status: latest?.overall_status ?? null,
      latest_duration: latest?.total_duration_seconds ?? null,
      latest_error:
        latest?.error_summary && Object.keys(latest.error_summary).length > 0
          ? latest.error_summary
          : null,
      recent: runs
    });
  }
  return summaries.sort((a, b) => a.dc_zone.localeCompare(b.dc_zone));
}

export const AssetDetailDrawer: React.FC<Props> = ({ asset, executions, onClose }) => {
  const isOpen = asset !== null;

  const filteredExecutions = useMemo(() => {
    if (!asset) return [];
    return executions.filter(
      e => e.tenant_id === asset.tenant_id && oemFromTaskName(e.actor_task_name) === asset.oem
    );
  }, [executions, asset]);

  const dcZoneSummaries = useMemo(
    () => summarizeByDcZone(filteredExecutions),
    [filteredExecutions]
  );

  const recentExecutions = useMemo(
    () => filteredExecutions.slice(0, 10),
    [filteredExecutions]
  );

  return (
    <Sheet open={isOpen} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent className='w-full sm:max-w-2xl overflow-hidden flex flex-col'>
        {asset && (
          <>
            <SheetHeader>
              <div className='flex items-center gap-2'>
                <SheetTitle>{asset.tenant_name} · {asset.oem}</SheetTitle>
                <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', STATUS_PILL[asset.status])}>
                  {asset.status}
                </span>
              </div>
              <SheetDescription>
                {/* These last two are different measures and routinely disagree by weeks,
                    so they are labelled rather than left as two bare facts side by side.
                    "counted as of" is the tenant-scoped crawl cutoff behind the number;
                    "refresh job ran" is when we last recomputed it. A zone can have been
                    recounted an hour ago off a crawl from six weeks ago. */}
                {asset.current_count.toLocaleString()} recalls
                {asset.latest_snapshot_date && ` · counted as of ${asset.latest_snapshot_date}`}
                {asset.hours_since_refresh != null &&
                  ` · refresh job ran ${asset.hours_since_refresh}h ago`}
              </SheetDescription>
            </SheetHeader>

            <ScrollArea className='flex-1 -mx-6 px-6 mt-2'>
              {/* Section 1: Per tenant-mapped zone */}
              <section className='mb-6'>
                <div className='flex items-baseline justify-between mb-2'>
                  <h3 className='text-sm font-semibold'>① Per tenant-mapped zone</h3>
                  <span className='text-[11px] text-muted-foreground'>
                    sum: {asset.current_count.toLocaleString()}
                  </span>
                </div>
                <div className='border rounded-md overflow-hidden'>
                  <div className='grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground'>
                    <span>Zone</span>
                    <span className='text-right'>Count</span>
                    <span
                      className='text-right'
                      title="Crawl cutoff each zone's count was taken at, for this tenant. Zones in the same OEM can differ by weeks — the row above shows only the newest."
                    >
                      As of
                    </span>
                  </div>
                  {asset.zones_in_oem.length === 0 ? (
                    <p className='text-xs text-muted-foreground p-3'>No zones for this OEM.</p>
                  ) : (
                    asset.zones_in_oem.map(z => (
                      <div
                        key={z.zone_id}
                        className='grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 border-t text-sm'
                      >
                        <div className='min-w-0'>
                          <p className='font-medium truncate'>{z.zone_name}</p>
                          {z.data_collection_zones.length > 0 && (
                            <p className='text-[11px] text-muted-foreground truncate'>
                              fed by: {z.data_collection_zones.join(', ')}
                            </p>
                          )}
                        </div>
                        <span className='text-right tabular-nums'>{z.count.toLocaleString()}</span>
                        <span className='text-right text-xs text-muted-foreground'>
                          {z.latest_snapshot_date ?? '—'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </section>

              {/* Section 2: Per data-collection zone */}
              <section className='mb-6'>
                <div className='flex items-baseline justify-between mb-2'>
                  <h3 className='text-sm font-semibold'>② Per data-collection zone</h3>
                  <span className='text-[11px] text-muted-foreground'>collector pipelines</span>
                </div>
                <p className='text-[11px] text-muted-foreground mb-2'>
                  No count column — DC zones overlap. Surfaces refresh status of each collector pipeline.
                </p>
                <div className='border rounded-md overflow-hidden'>
                  <div className='grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground'>
                    <span>DC zone</span>
                    <span className='text-right'>Latest run</span>
                    <span className='text-right'>Status</span>
                    <span className='text-right'>Duration</span>
                  </div>
                  {dcZoneSummaries.length === 0 ? (
                    <p className='text-xs text-muted-foreground p-3'>
                      No collector executions for this OEM.
                    </p>
                  ) : (
                    dcZoneSummaries.map(s => (
                      <div
                        key={s.dc_zone}
                        className='grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3 py-2 border-t text-sm'
                      >
                        <div className='min-w-0'>
                          <p className='font-medium truncate font-mono text-xs'>{s.dc_zone}</p>
                          {s.latest_error && (
                            <p
                              className='text-[11px] text-red-600 dark:text-red-400 truncate'
                              title={JSON.stringify(s.latest_error)}
                            >
                              error: {JSON.stringify(s.latest_error).slice(0, 60)}
                            </p>
                          )}
                        </div>
                        <span className='text-right text-xs text-muted-foreground'>
                          {s.latest_at ? new Date(s.latest_at).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric'
                          }) : '—'}
                        </span>
                        <span className='text-right flex items-center justify-end gap-1.5 text-xs'>
                          {s.latest_status && (
                            <span
                              className={cn(
                                'h-2 w-2 rounded-full',
                                EXEC_STATUS_DOT[s.latest_status] ?? 'bg-muted-foreground/40'
                              )}
                            />
                          )}
                          {s.latest_status ?? '—'}
                        </span>
                        <span className='text-right text-xs tabular-nums'>
                          {fmtDuration(s.latest_duration)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </section>

              {/* Section 3: Recent executions */}
              <section className='mb-6'>
                <h3 className='text-sm font-semibold mb-2'>Recent executions</h3>
                {recentExecutions.length === 0 ? (
                  <p className='text-xs text-muted-foreground'>No executions yet.</p>
                ) : (
                  <ul className='border rounded-md divide-y'>
                    {recentExecutions.map(e => (
                      <li key={e.id} className='px-3 py-2 text-sm'>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='font-mono text-xs truncate'>{e.actor_task_name}</span>
                          <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                            <span
                              className={cn(
                                'h-2 w-2 rounded-full',
                                EXEC_STATUS_DOT[e.overall_status] ?? 'bg-muted-foreground/40'
                              )}
                            />
                            {e.overall_status}
                          </span>
                        </div>
                        <div className='flex justify-between text-[11px] text-muted-foreground pt-0.5'>
                          <span>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</span>
                          <span>
                            {e.snapshot_inserted_records != null && (
                              <>+{e.snapshot_inserted_records.toLocaleString()} inserts · </>
                            )}
                            {fmtDuration(e.total_duration_seconds)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AssetDetailDrawer;
